/**
 * UYGULAMA VERİ KATMANI — depo seçimi ve kalıcılık.
 *
 * Bu dosya, uygulamanın "sunucu var mı, veri nerede" sorusunu tek yere
 * toplar. Bileşenler yalnızca `Repository` arayüzünü görür; `localStorage`
 * kelimesi burada bile yalnızca açıklama içinde geçer.
 *
 * YAZIM İLKESİ: Ağ gecikmesi oyunu yavaşlatmamalıdır. Bu yüzden her yazma
 * ÖNCE yerel duruma yansıtılır (anında görünür), sonra ARKA PLANDA
 * sunucuya gönderilir. Sunucu yavaş olsa bile can puanı değişimi anlıktır.
 */

import { CONTENT, KAELEN_IDENTITY, KAELEN_SPELLS, KAELEN_VEYL } from '@tttrpg/content'
import {
  type Backend,
  type CharacterRow,
  createLocalRepository,
  createRepository,
  type Repository,
} from '@tttrpg/db/client'
import { aggregateCharacter, type ContentIndex } from '@tttrpg/rules'
import type { SheetIdentity } from '@tttrpg/ui'
import { useCallback, useEffect, useRef, useState } from 'react'

// ---------------------------------------------------------------------------
// DEPO SEÇİMİ
// ---------------------------------------------------------------------------

export interface Store {
  repository: Repository
  backend: Backend
  /** Sunucuya ulaşılamadıysa neden? Kullanıcıya gösterilir. */
  notice: string | null
  /** Depo hazır mı? İlk yükleme. */
  ready: boolean
}

export function useStore(): Store {
  const [store, setStore] = useState<Store>(() => ({
    repository: createLocalRepository(),
    backend: 'local',
    notice: null,
    ready: false,
  }))

  useEffect(() => {
    let cancelled = false

    void (async () => {
      // Önce yerel depo kurulur; Worker'a ulaşılamazsa veri kaybolmaz.
      const local = createLocalRepository()

      try {
        const { repository, fallbackReason } = await createRepository(
          {
            apiUrl: import.meta.env.VITE_ZARHANE_API,
            debug: import.meta.env.VITE_ZARHANE_DEBUG === 'true',
          },
          local,
        )

        if (cancelled) return
        setStore({
          repository,
          backend: repository.backend,
          notice: fallbackReason ?? null,
          ready: true,
        })
      } catch (error) {
        // ⚠️ BU CATCH OLMAZSA UYGULAMA SONSUZA DEK AÇILIŞ EKRANINDA KALIR.
        // Depo fabrikası beklenmedik bir şey atarsa (bozuk ortam değişkeni,
        // localStorage'ın tamamen reddedilmesi, ağ hatası...) kullanıcı yine de
        // uygulamayı açabilmelidir: yerel depoya düş ve sebebi göster.
        if (cancelled) return
        const reason = error instanceof Error ? error.message : 'bilinmeyen hata'
        setStore({
          repository: local,
          backend: 'local',
          notice: `Sunucu başlatılamadı (${reason}). Yerel modda çalışılıyor.`,
          ready: true,
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return store
}

// ---------------------------------------------------------------------------
// KARAKTER DEPOSU
// ---------------------------------------------------------------------------

/** Yazma gecikmesi (ms). Kısa: kullanıcı iki kere tıklayınca tek istek olur. */
const WRITE_DEBOUNCE_MS = 500

export interface CharacterStore {
  rows: CharacterRow[]
  loading: boolean
  error: string | null
  /** Anında görünmesi için yerel durumu günceller, sonra kaydeder. */
  patch(id: string, changes: Partial<CharacterRow>): void
  /** Yeni karakter oluşturur ve kimliğini döner. */
  create(row: Omit<CharacterRow, 'id' | 'createdAt' | 'updatedAt'>): Promise<string>
  remove(id: string): Promise<void>
  reload(): Promise<void>
  /** Paylaşım bağlantısını üretir. */
  share(id: string): Promise<string | null>
}

export function useCharacters(repository: Repository): CharacterStore {
  const [rows, setRows] = useState<CharacterRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Bekleyen yazmalar: karakter kimliği → gönderilmemiş değişiklikler
  const pending = useRef(new Map<string, Partial<CharacterRow>>())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const reload = useCallback(async () => {
    try {
      setRows(await repository.listCharacters())
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Karakterler okunamadı.')
    } finally {
      setLoading(false)
    }
  }, [repository])

  useEffect(() => {
    void reload()
  }, [reload])

  /** Tek karakterin bekleyen yazmasını sunucuya gönderir. */
  const flush = useCallback(
    async (id: string) => {
      const changes = pending.current.get(id)
      if (!changes) return
      pending.current.delete(id)
      const timer = timers.current.get(id)
      if (timer) clearTimeout(timer)
      timers.current.delete(id)

      try {
        const saved = await repository.updateCharacter(id, changes)
        // Sunucu `updatedAt`'i yönetir; yerel karşılığı tazele.
        setRows((prev) =>
          prev.map((row) => (row.id === id ? { ...row, updatedAt: saved.updatedAt } : row)),
        )
        setError(null)
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Kaydedilemedi.')
      }
    },
    [repository],
  )

  // Sekme kapanırken bekleyen yazmaları gönder. HP kaybı kaybolmasın.
  useEffect(() => {
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') {
        for (const id of [...pending.current.keys()]) void flush(id)
      }
    }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [flush])

  // Depo değişirse bekleyen yazmaları hemen gönder (mod değiştirme).
  useEffect(() => {
    return () => {
      for (const id of [...pending.current.keys()]) void flush(id)
    }
  }, [flush])

  const patch = useCallback(
    (id: string, changes: Partial<CharacterRow>) => {
      // 1) Anında görünmesi için yerel durumu güncelle.
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...changes } : row)))

      // 2) Sunucuya gönderilecek değişikliği biriktir.
      const merged = { ...(pending.current.get(id) ?? {}), ...changes }
      pending.current.set(id, merged)

      const existing = timers.current.get(id)
      if (existing) clearTimeout(existing)
      timers.current.set(
        id,
        setTimeout(() => void flush(id), WRITE_DEBOUNCE_MS),
      )
    },
    [flush],
  )

  const create = useCallback(
    async (row: Omit<CharacterRow, 'id' | 'createdAt' | 'updatedAt'>) => {
      const created = await repository.createCharacter(row)
      setRows((prev) => [...prev, created])
      return created.id
    },
    [repository],
  )

  const remove = useCallback(
    async (id: string) => {
      const timer = timers.current.get(id)
      if (timer) clearTimeout(timer)
      timers.current.delete(id)
      pending.current.delete(id)

      setRows((prev) => prev.filter((row) => row.id !== id))
      await repository.deleteCharacter(id)
      await reload()
    },
    [repository, reload],
  )

  const share = useCallback(
    async (id: string) => {
      const token = await repository.shareCharacter(id)
      if (token) {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, shareToken: token } : row)))
      }
      return token
    },
    [repository],
  )

  return { rows, loading, error, patch, create, remove, reload, share }
}

// ---------------------------------------------------------------------------
// KİMLİK TÜRETME
// ---------------------------------------------------------------------------

/**
 * Sheet'in başlığındaki kimlik bilgisi.
 *
 * NEDEN SAKLANMIYOR? `identity` alanları (`raceName`, `className`...) içerik
 * adlarına BAĞLIDIR. Saklansaydı, içerikte bir isim değiştiğinde eski
 * karakterlerde eski isim kalırdı. Türetilerek saklanır: içerik değişirse
 * TÜM karakterler kendiliğinden doğru adı gösterir.
 *
 * Bu, "hesaplanmışı değil girdiyi sakla" ilkesinin UI'ya yansımasıdır.
 */
export function deriveIdentity(row: CharacterRow, content: ContentIndex = CONTENT): SheetIdentity {
  const race = row.data.raceId ? content.races[row.data.raceId] : null
  const classId = row.data.classes[0]?.classId
  const cls = classId ? content.classes[classId] : null
  const background = row.data.backgroundId ? content.backgrounds[row.data.backgroundId] : null

  return {
    name: row.name,
    alignment: row.alignment,
    className: cls?.name ?? '—',
    raceName: race?.name ?? '—',
    backgroundName: background?.name ?? '—',
    rank: background?.name,
  }
}

// ---------------------------------------------------------------------------
// DEMO KARAKTER
// ---------------------------------------------------------------------------

/**
 * Hazır karakter (Kaelen) — yalnızca YEREL modda ve liste boşken eklenir.
 *
 * Sunucu modunda hiçbir şey otomatik oluşturulmaz: her kullanıcı kendi
 * karakterini kendisi kurar. Aksi halde yeni katılan oyuncuya başkasının
 * karakteri yüklenirdi.
 */
export function seedCharacterRow(): CharacterRow {
  const sheet = aggregateCharacter(KAELEN_VEYL, CONTENT)

  return {
    id: 'kaelen-veyl',
    name: KAELEN_IDENTITY.name,
    alignment: KAELEN_IDENTITY.alignment,
    level: KAELEN_VEYL.level,
    data: KAELEN_VEYL,
    session: {
      currentHp: sheet.defense.hitPoints,
      tempHp: 0,
      // Hazırlanan büyüler oturum VERİSİDİR (kural değil). Bu yüzden
      // kurallardan türetilmez; oyuncu hazırladıkça güncellenir.
      cantrips: KAELEN_SPELLS.cantrips,
      prepared: KAELEN_SPELLS.prepared,
      poolDefs: KAELEN_SPELLS.pools,
      pools: Object.fromEntries(KAELEN_SPELLS.pools.map((pool) => [pool.id, pool.max])),
    },
    shareToken: null,
    createdAt: 0,
    updatedAt: 0,
  }
}
